

import {Component} from "vue-property-decorator";
import {UserLevel} from "shared/entities/user";
import * as icons from "@fortawesome/free-solid-svg-icons";
import TrainingSection from "@/components/training-section.vue";
import BrandManualSection from "@/components/brand-manual-section.vue";
import ManagementSection from "@/components/managment-section.vue";
import ImportantLinksSection     from "@/components/important-links-section.vue";
import {mixins} from "vue-class-component";
import AuthMixin from "@/mixins/auth.mixin";
import DocumentsSection from "@/components/documents-section.vue";
import {Api} from "shared/api";
import {IAlert} from "shared/entities/alert";

const VIEW_KEY = "chickenshack.homeView";

interface HomeSection {
    key: string;
    label: string;
    icon: string;
    minLevel: UserLevel;
}

// Same gates as the v1 layout: brand manual is Owner+, management is GM+,
// everything else is visible to any signed-in employee.
const SECTIONS: HomeSection[] = [
    {key: "training",   label: "Training Material", icon: "faGraduationCap",   minLevel: UserLevel.Employee},
    {key: "brand",      label: "Brand Standards",   icon: "faBook",            minLevel: UserLevel.Owner},
    {key: "links",      label: "Important Links",   icon: "faExternalLinkAlt", minLevel: UserLevel.Employee},
    {key: "documents",  label: "Documents",         icon: "faFolderOpen",      minLevel: UserLevel.Employee},
    {key: "management", label: "Management",        icon: "faSlidersH",        minLevel: UserLevel.GM},
];

@Component({
    components: {DocumentsSection, ManagementSection, BrandManualSection, TrainingSection, ImportantLinksSection},
})
export default class Home extends mixins(AuthMixin) {
    UserLevel = UserLevel;
    icons = icons;
    v2 = false;
    activeSection = "training";
    activeAlerts: IAlert[] = []; // Define the type for activeAlerts
    isModalVisible = false; // Declare isModalVisible

    private scrollQueued = false;
    private onScroll = () => {
        // rAF-throttled: scroll fires far more often than we need to recompute.
        if (this.scrollQueued) return;
        this.scrollQueued = true;
        window.requestAnimationFrame(() => {
            this.scrollQueued = false;
            this.updateActiveSection();
        });
    };

get visibleSections(): HomeSection[] {
    if (!this.user) return [];
    return SECTIONS.filter(s => this.user.level >= s.minLevel);
}

async mounted() {
    this.loadView();
    window.addEventListener("scroll", this.onScroll, {passive: true});
    await this.fetchAlerts();
}

beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
}

setV2(value: boolean) {
    this.v2 = value;
    try {
        window.localStorage.setItem(VIEW_KEY, value ? "v2" : "v1");
    } catch (e) {
        // Private browsing or storage disabled — the toggle still works for this session.
    }
}

scrollToSection(key: string) {
    this.activeSection = key;
    const el = document.getElementById(`section-${key}`);
    if (!el) return;
    if (typeof el.scrollIntoView === "function") {
        el.scrollIntoView({behavior: "smooth", block: "start"});
    }
}

private updateActiveSection() {
    if (!this.v2) return;
    // Whichever section's top has most recently passed under the sticky bar.
    const threshold = 90;
    let current = this.visibleSections.length > 0 ? this.visibleSections[0].key : "training";
    for (const section of this.visibleSections) {
        const el = document.getElementById(`section-${section.key}`);
        if (el && el.getBoundingClientRect().top <= threshold) {
            current = section.key;
        }
    }
    this.activeSection = current;
}

private loadView() {
    try {
        // v1 is the default: only an explicit "v2" opts in.
        this.v2 = window.localStorage.getItem(VIEW_KEY) === "v2";
    } catch (e) {
        this.v2 = false;
    }
    const allowed = this.visibleSections.map(s => s.key);
    this.activeSection = allowed.length > 0 ? allowed[0] : "training";
}

async fetchAlerts() {
    try {
        const alerts = await Api.alerts.Get();
        const today = new Date();
        this.activeAlerts = alerts.filter(alert => {
            const creationDate = new Date(alert.creationDate);
            const expirationDate = new Date(alert.expirationDate);
            return creationDate <= today && expirationDate >= today;
        });
        if (this.activeAlerts.length > 0) {
            this.isModalVisible = true; // Show modal if there are alerts
        };
    } catch (error) {
        // eslint-disable-next-line
        console.error("Error fetching alerts:", error);
    }
}

closeAlert() {
    this.activeAlerts = []; // Clear all alerts
    this.isModalVisible = false; // Hide the modal
}

}
